import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Particles } from "@/components/Particles";
import { Separator } from "@/components/ui/separator";
import { EmailAuthForm } from "@/components/EmailAuthForm";
import { ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";

const Auth = () => {
  const { signInWithGoogle, signInWithApple, user, loading } = useAuth();
  const navigate = useNavigate();
  const [showEmailForm, setShowEmailForm] = useState(false);

  useEffect(() => {
    if (user && !loading) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      toast.error("Error logging in with Google");
    }
  };

  const handleAppleSignIn = async () => {
    try {
      await signInWithApple();
    } catch (error) {
      toast.error("Error logging in with Apple");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: "hsl(var(--gold))" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Preload promo video */}
      <video src="https://assets.loverslabs.art/videos/web_optimized2.mp4" preload="auto" className="hidden" muted />

      {/* Premium Background */}
      <div className="fixed inset-0 bg-deep-black">
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[120px] opacity-20"
          style={{ background: "" }}
        />
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-[100px] opacity-15"
          style={{
            background: "",
            animationDelay: "",
          }}
        />
      </div>

      <Particles />

      <div className="relative z-10 min-h-screen flex items-start md:items-center justify-center p-4 pt-20 md:pt-6 overflow-y-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          className="fixed top-4 left-4 z-50 text-foreground/70 hover:text-gold transition-colors bg-background/50 backdrop-blur-sm"
        >
          <ArrowLeft className="h-6 w-6" />
        </Button>
        <Card className="w-full max-w-md glass-premium border-premium mb-6">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center mb-4">
              <img src="https://assets.loverslabs.art/logoheroBSwh3kw.png" alt="" className="h-14 md:h-20 w-auto" />
            </div>
            <CardTitle
              className="text-3xl font-bold bg-clip-text text-transparent"
              style={{
                backgroundImage: "var(--gradient-premium)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
              }}
            >
              Welcome
            </CardTitle>
            <CardDescription className="text-foreground/70 text-base">
              Sign in with Google or Apple — start creating for free.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={handleGoogleSignIn}
              className="w-full gradient-premium hover:opacity-90 text-white border-0 transition-all shadow-lg hover:shadow-xl"
              size="lg"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </Button>

            <Button
              onClick={handleAppleSignIn}
              className="w-full bg-white hover:bg-white/90 text-black border-0 transition-all shadow-lg hover:shadow-xl"
              size="lg"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                />
              </svg>
              Sign in with Apple
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            {!showEmailForm ? (
              <Button
                onClick={() => setShowEmailForm(true)}
                variant="outline"
                className="w-full glass-premium border-premium"
                size="lg"
              >
                <Mail className="w-5 h-5 mr-2" />
                Email
              </Button>
            ) : (
              <EmailAuthForm />
            )}

            <p className="text-xs text-center text-foreground/50 mt-4">
              By continuing, you confirm that you are of legal age in your jurisdiction and agree to our{" "}
              <a href="/termsofservice" className="underline hover:text-gold transition-colors">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacypolicy" className="underline hover:text-gold transition-colors">
                Privacy Policy
              </a>
              .
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
